/**
 * Pailena · taille à choisir (bagues à taille fixe)
 * Horizon coche d'office la première taille disponible : un client pouvait commander une taille
 * qu'il n'avait jamais choisie (audit UX-01). Pour chaque sélecteur qui a une option « Taille »
 * à plusieurs valeurs, on décoche la taille à l'arrivée et l'ajout au panier attend le choix du
 * client. Couvre la fiche produit, la barre d'achat collante et la fenêtre « Choisir » des listes.
 * Une adresse avec ?variant= (lien partagé, retour depuis le panier) compte comme un choix.
 */

const LEGEND = /^\s*taille/i;
const HINT = 'Choisissez votre taille';
const ALERT = 'Choisissez votre taille avant d’ajouter au panier.';

/** Produits pour lesquels le client a choisi sa taille (clé : identifiant produit). */
const chosen = new Set();
if (new URL(window.location.href).searchParams.has('variant')) {
  const picker = document.querySelector('variant-picker[data-template-product-match="true"]');
  const id = picker?.getAttribute('data-product-id');
  if (id) chosen.add(id);
}

/** @param {Element} picker */
function sizeFieldset(picker) {
  for (const fieldset of picker.querySelectorAll('fieldset.variant-option')) {
    const legend = fieldset.querySelector('legend');
    if (legend && LEGEND.test(legend.textContent || '') && fieldset.querySelectorAll('input[type="radio"]').length > 1) {
      return /** @type {HTMLFieldSetElement} */ (fieldset);
    }
  }
  return null;
}

/** Conteneur commun au sélecteur et à son bouton d'ajout. @param {Element} element */
function scope(element) {
  return element.closest('dialog, .quick-add-modal, .product-details, .product-information, product-card') || document;
}

/** @param {Element} picker */
function isPending(picker) {
  const id = picker.getAttribute('data-product-id');
  return Boolean(id && sizeFieldset(picker) && !chosen.has(id));
}

/** Décoche la taille et affiche l'invite, tant que le client n'a pas choisi. */
function apply() {
  for (const picker of document.querySelectorAll('variant-picker')) {
    const fieldset = sizeFieldset(picker);
    if (!fieldset) continue;
    const pending = isPending(picker);
    fieldset.classList.toggle('pl-taille--attente', pending);
    let hint = fieldset.querySelector('.pl-taille__invite');
    if (pending) {
      for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
        /** @type {HTMLInputElement} */ (input).checked = false;
      }
      if (!hint) {
        hint = document.createElement('span');
        hint.className = 'pl-taille__invite';
        hint.textContent = HINT;
        fieldset.querySelector('legend')?.append(hint);
      }
    } else {
      hint?.remove();
      fieldset.querySelector('.pl-taille__alerte')?.remove();
    }
  }
}

/** Bloque l'ajout et montre où choisir. @param {Element} picker */
function block(picker) {
  const fieldset = sizeFieldset(picker);
  if (!fieldset) return;
  let alert = fieldset.querySelector('.pl-taille__alerte');
  if (!alert) {
    alert = document.createElement('p');
    alert.className = 'pl-taille__alerte';
    alert.setAttribute('role', 'alert');
    fieldset.append(alert);
  }
  alert.textContent = '';
  window.setTimeout(() => {
    if (alert) alert.textContent = ALERT;
  }, 30);
  fieldset.classList.remove('pl-taille--secousse');
  void fieldset.offsetWidth;
  fieldset.classList.add('pl-taille--secousse');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  fieldset.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
  /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input[type="radio"]:not([aria-disabled="true"])'))?.focus({ preventScroll: true });
}

/** Sélecteur concerné par un bouton d'ajout, s'il attend une taille. @param {Element} trigger */
function pendingPickerFor(trigger) {
  // La barre collante pilote le bouton de la fiche produit.
  const root = trigger.closest('.sticky-add-to-cart') ? document.querySelector('.product-details') || document : scope(trigger);
  for (const picker of root.querySelectorAll('variant-picker')) {
    if (isPending(picker)) return picker;
  }
  return null;
}

// Choix du client : toute taille cliquée vaut choix pour ce produit.
document.addEventListener(
  'change',
  (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    const picker = input.closest('variant-picker');
    const fieldset = picker && sizeFieldset(picker);
    if (!picker || !fieldset || !fieldset.contains(input)) return;
    const id = picker.getAttribute('data-product-id');
    if (id) chosen.add(id);
    apply();
  },
  { capture: true }
);

// Ajout au panier sans taille : on bloque avant les gestionnaires d'Horizon.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest('button[name="add"], .sticky-add-to-cart__button');
    if (!trigger) return;
    const picker = pendingPickerFor(trigger);
    if (!picker) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    block(picker);
  },
  { capture: true }
);

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
    const picker = pendingPickerFor(form);
    if (!picker) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    block(picker);
  },
  { capture: true }
);

// Horizon re-rend le sélecteur (changement de couleur, ouverture de « Choisir ») : on réapplique.
apply();
new MutationObserver(() => {
  window.clearTimeout(/** @type {any} */ (apply).timer);
  /** @type {any} */ (apply).timer = window.setTimeout(apply, 60);
}).observe(document.body, { childList: true, subtree: true });

/* ---------- Variante dans l'adresse (audit UX-18) ---------- */
// Horizon prévoit d'écrire ?variant= à chaque choix, mais l'écriture n'a pas lieu en ligne : un lien partagé
// ou un retour arrière perdait la couleur ou la taille choisie. On l'écrit après la mise à jour du formulaire.
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
  const picker = input.closest('variant-picker[data-template-product-match="true"]');
  if (!picker || input.closest('product-card, quick-add-dialog, dialog')) return;
  window.setTimeout(() => {
    // Taille encore à choisir : pas de variante dans l'adresse, sinon un rechargement la croirait choisie.
    const current = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (current && isPending(current)) return;
    const field = document.querySelector('.product-details form[data-type="add-to-cart-form"] input[name="id"]');
    const id = field instanceof HTMLInputElement ? field.value : input.dataset.variantId;
    if (!id) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') === id) return;
    url.searchParams.set('variant', id);
    history.replaceState(history.state, '', url.toString());
  }, 900);
});
